import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import { useState } from 'react';

const solutions = [
  {
    icon: <FastfoodIcon sx={{ fontSize: 48, color: '#234734' }} />,
    title: 'Quick Service Restaurants (QSRs)',
    description: 'Optimize speed and efficiency for high-volume operations',
    features: [
      'Fast order processing and payment',
      'Drive-thru management',
      'Mobile ordering integration',
      'Real-time kitchen display system',
    ],
  },
  {
    icon: <RestaurantIcon sx={{ fontSize: 48, color: '#234734' }} />,
    title: 'Fine Dining',
    description: 'Elevate the dining experience with sophisticated features',
    features: [
      'Advanced reservation management',
      'Wine inventory and pairing',
      'Table-side ordering',
      'Detailed guest preferences tracking',
    ],
  },
  {
    icon: <LocalCafeIcon sx={{ fontSize: 48, color: '#234734' }} />,
    title: 'Cafes & Bakeries',
    description: 'Perfect for coffee shops and bakeries',
    features: [
      'Recipe and ingredient tracking',
      'Bakery inventory management',
      'Loyalty program integration',
      'Mobile order-ahead system',
    ],
  },
  {
    icon: <LocalDiningIcon sx={{ fontSize: 48, color: '#234734' }} />,
    title: 'Food Trucks',
    description: 'Mobile POS solution for food trucks',
    features: [
      'Offline mode for remote locations',
      'GPS location tracking',
      'Inventory on the go',
      'Mobile payment processing',
    ],
  },
  {
    icon: <LocalBarIcon sx={{ fontSize: 48, color: '#234734' }} />,
    title: 'Bars & Breweries',
    description: 'Specialized features for beverage businesses',
    features: [
      'Liquor inventory management',
      'Pour tracking and waste control',
      'Happy hour automation',
      'Brewery production tracking',
    ],
  },
];

function Solutions() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'linear-gradient(90deg, #fff8ec 0%, #F9B233 100%)',
          color: '#234734',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            align="center"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
              color: '#234734',
              textShadow: '0 2px 8px rgba(249,178,51,0.08)',
            }}
          >
            Solutions for Every Restaurant Type
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{ opacity: 0.9, color: '#234734' }}
          >
            Tailored features to meet your specific business needs
          </Typography>
        </Container>
      </Box>

      {/* Tabbed Solutions */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 4,
            bgcolor: '#fff8ec',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(249,178,51,0.06)',
            '& .MuiTab-root': {
              color: '#234734',
              fontWeight: 700,
              fontSize: '1rem',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              '&.Mui-selected': {
                bgcolor: '#F9B233',
                color: '#234734',
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: '#F9B233',
            },
          }}
        >
          {solutions.map((solution, idx) => (
            <Tab key={solution.title} label={solution.title} />
          ))}
        </Tabs>
        <Box sx={{ mt: 2 }}>
          <Card
            sx={{
              bgcolor: '#fff8ec',
              border: '1.5px solid #F9B233',
              boxShadow: '0 2px 12px rgba(249,178,51,0.06)',
              p: 3,
              minHeight: 340,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Box sx={{ mb: 2 }}>{solutions[tab].icon}</Box>
            <Typography variant="h3" sx={{ color: '#234734', fontWeight: 700, mb: 1 }}>
              {solutions[tab].title}
            </Typography>
            <Typography sx={{ color: '#234734', opacity: 0.8, mb: 2 }}>
              {solutions[tab].description}
            </Typography>
            <List>
              {solutions[tab].features.map((feature, idx) => (
                <ListItem key={idx} sx={{ py: 0.5, justifyContent: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon sx={{ color: '#F9B233' }} />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </Card>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              bgcolor: '#234734',
              color: '#F9B233',
              borderRadius: 3,
              boxShadow: '0 4px 24px rgba(35,71,52,0.10)',
            }}
          >
            <Typography variant="h3" gutterBottom>
              Ready to Find Your Perfect Solution?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Let us help you choose the right features for your business
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              sx={{
                bgcolor: '#F9B233',
                color: '#234734',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(249,178,51,0.10)',
                '&:hover': { bgcolor: '#ffe1a6', color: '#234734' },
              }}
              size="large"
            >
              Schedule a Consultation
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Solutions; 